// src/api/apiClient.js
import axios from "axios";
import { deepSanitizePayload, validateDocumentFile } from "../utils/inputValidation.js";

const rawBaseUrl =
  process.env.REACT_APP_M2 ||
  process.env.REACT_APP_BASE_URL ||
  process.env.REACT_APP_API_URL ||
  "http://localhost:4000";

// Ensure base URL doesn't end with a trailing slash
export const API_BASE_URL = rawBaseUrl.replace(/\/+$/, "");

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  // CRITICAL: Enables browser to automatically send and receive HttpOnly cookies
  withCredentials: true,
  xsrfCookieName: "XSRF-TOKEN",
  xsrfHeaderName: "X-XSRF-TOKEN",
});

// ─── REQUEST INTERCEPTOR: Path Normalization & Silent Sanitization ───
apiClient.interceptors.request.use(
  (config) => {
    // Prevent duplicate /api when baseURL already ends with /api
    if (config.url && typeof config.url === "string") {
      if (API_BASE_URL.endsWith("/api") && config.url.startsWith("/api/")) {
        config.url = config.url.replace(/^\/api/, "");
      }
    }

    // ─── ANTI-TAMPER & DEVTOOLS BYPASS DEFENSE: SILENT SANITIZATION ───
    // If a user forces an action via DevTools (inspect element, removing disabled,
    // or invoking client calls via console), silently sanitize all data, params,
    // and query strings before network transmission so no script injection reaches the server.
    // ─── MULTIPART / FORMDATA HANDLING ───
    const isFormData =
      (typeof FormData !== "undefined" && config.data instanceof FormData) ||
      config.data?.constructor?.name === "FormData";

    // Silently sanitize non-FormData payloads before transmission
    if (config.data && !isFormData) {
      config.data = deepSanitizePayload(config.data);
    }

    // If payload is FormData, remove hardcoded Content-Type so Axios/browser sets boundary automatically
    if (isFormData) {
      if (config.headers) {
        delete config.headers["Content-Type"];
        delete config.headers["content-type"];
        if (typeof config.headers.delete === "function") {
          config.headers.delete("Content-Type");
          config.headers.delete("content-type");
        }
      }

      // Security check: Validate any File objects attached to FormData to block DevTools / script bypass
      if (config.data && typeof config.data.entries === "function") {
        for (const [, value] of config.data.entries()) {
          const isFile =
            (typeof File !== "undefined" && value instanceof File) ||
            (typeof Blob !== "undefined" && value instanceof Blob);
          if (isFile && value.name) {
            const validation = validateDocumentFile(value);
            if (!validation.valid) {
              return Promise.reject(
                new Error(`Upload blocked by security policy: ${validation.error}`)
              );
            }
          }
        }
      }
    }

    // Silent sanitization of query parameters (GET/search requests)
    if (config.params) {
      config.params = deepSanitizePayload(config.params);
    }

    // Silent sanitization of embedded URL query strings
    if (typeof config.url === "string" && config.url.includes("?")) {
      try {
        const [urlPath, queryString] = config.url.split("?");
        const searchParams = new URLSearchParams(queryString);
        const cleanParams = new URLSearchParams();
        for (const [key, val] of searchParams.entries()) {
          cleanParams.append(key, deepSanitizePayload(val));
        }
        config.url = `${urlPath}?${cleanParams.toString()}`;
      } catch (urlErr) {
        // Fallback: keep original URL
      }
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// ─── IN-FLIGHT REFRESH QUEUE (MUTEX) ───
let isRefreshing = false;
let failedQueue = [];
let isLoggingOut = false;

const processQueue = (error) => {
  failedQueue.forEach((promise) => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve();
    }
  });
  failedQueue = [];
};

// ─── RESPONSE INTERCEPTOR ───
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If no response or originalRequest is missing, reject
    if (!error.response || !originalRequest) {
      return Promise.reject(error);
    }

    // Handle 403 Forbidden (RBAC Access Denied)
    if (error.response.status === 403) {
      console.warn("[RBAC] Access denied: User lacks required permissions.");
      if (typeof window !== "undefined") {
        const forbiddenMsg =
          error.response.data?.message ||
          error.response.data?.msg ||
          error.response.data?.error ||
          "Access denied: You do not have permission to perform this action.";
        window.dispatchEvent(
          new CustomEvent("auth:forbidden", {
            detail: { message: forbiddenMsg, error },
          })
        );
      }
      return Promise.reject(error);
    }

    // Ignore non-401 errors or login endpoint itself
    if (
      error.response.status !== 401 ||
      (originalRequest.url && originalRequest.url.includes("/login"))
    ) {
      return Promise.reject(error);
    }

    // /api/me 401 simply means "not authenticated" — let the caller
    // (checkSession) handle it directly; do NOT attempt a refresh.
    if (
      originalRequest.url &&
      (originalRequest.url.includes("/me") ||
        originalRequest.url.endsWith("/me"))
    ) {
      return Promise.reject(error);
    }

    // If a logout is already in progress, don't start another refresh cycle
    if (isLoggingOut) {
      return Promise.reject(error);
    }

    // Check if server indicated an explicit session revocation or security violation
    const responseData = error.response.data;
    if (responseData?.sessionRevoked || responseData?.securityViolation) {
      const reason = responseData?.securityViolation
        ? "Session terminated due to a security violation. Please log in again."
        : "Your session has expired or was revoked. Please log in again.";
      handleGlobalLogout(reason);
      return Promise.reject(error);
    }

    // Avoid infinite loop if refresh endpoint itself failed with 401
    if (
      originalRequest.url &&
      (originalRequest.url.includes("/refresh-token") ||
        originalRequest.url.includes("/refresh"))
    ) {
      handleGlobalLogout("Session expired. Please log in again.");
      return Promise.reject(error);
    }

    // If request already retried once, reject
    if (originalRequest._retry) {
      return Promise.reject(error);
    }

    // If a refresh is already in progress, queue this request
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
        .then(() => apiClient(originalRequest))
        .catch((err) => Promise.reject(err));
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      // Refresh token is automatically passed via HttpOnly cookie
      await apiClient.post("/api/refresh-token", {});
      // Flush queue: replay all requests waiting for the new token
      processQueue(null);
      // Retry the original request that triggered the 401
      return apiClient(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError);
      handleGlobalLogout("Session expired. Please log in again.");
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  },
);

export const handleGlobalLogout = (reason) => {
  if (typeof window === "undefined") return;

  // Re-entrancy guard: prevent cascading logout storms
  if (isLoggingOut) return;
  isLoggingOut = true;

  // Clear non-cookie in-memory / storage items
  try {
    localStorage.removeItem("user_info");
    localStorage.removeItem("user_data");
    localStorage.removeItem("token");
    sessionStorage.clear();
  } catch (e) {
    console.warn("Storage clear error:", e);
  }

  if (reason) {
    console.warn(`[AUTH] ${reason}`);
  }

  // Broadcast across tabs
  try {
    if (typeof BroadcastChannel !== "undefined") {
      const channel = new BroadcastChannel("hospital_auth_channel");
      channel.postMessage({
        type: "LOGOUT",
        reason: reason || "session_expired",
      });
    }
  } catch (e) {}

  // Only dispatch auth:unauthorized if not already on the login page
  // to prevent a secondary logout chain via authService.notifySessionTerminated
  if (window.location.pathname !== "/login") {
    try {
      window.dispatchEvent(
        new CustomEvent("auth:unauthorized", {
          detail: { message: reason || "Session expired." },
        }),
      );
    } catch (e) {}
  }

  // Redirect to login page if not already there
  if (window.location.pathname !== "/login") {
    window.location.href = `/login?reason=${encodeURIComponent(
      reason || "session_expired",
    )}`;
  }

  // Reset guard after a tick so future legitimate logouts still work
  setTimeout(() => {
    isLoggingOut = false;
  }, 500);
};

export default apiClient;
